import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SavingsIcon from "@mui/icons-material/Savings";
import { useAuth0 } from "@auth0/auth0-react";
import { AUTH0_AUDIENCE, MGMT } from "../env";
import { useAuthApi } from "../hooks/useAuthApi";
import { fmtDate } from "../format";
import type {
  FinanceAuditEvent,
  FinanceDunningCase,
  Driver,
  FinanceDocument,
  FinancePayout,
  FinanceReconciliationBatch,
  FinanceSummary,
  FinanceTaxProfile,
  HomeChargingMonthlyReport,
  HomeChargingProfile,
  InvoiceRecord,
} from "../types";
import { fullDriversFromResponse, type DriverListResponse } from "../driverResponses";
import { useToast } from "../toast";

function currentMonth() {
  return new Date().toISOString().slice(0, 7);
}

export default function Reports() {
  const api = useAuthApi();
  const { getAccessTokenSilently } = useAuth0();
  const toast = useToast();

  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driverId, setDriverId] = useState<number | "">("");
  const [month, setMonth] = useState(currentMonth());
  const [sessionId, setSessionId] = useState("");

  const [invoice, setInvoice] = useState<InvoiceRecord | null>(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [sessionInvoiceLoading, setSessionInvoiceLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);

  const [profile, setProfile] = useState({
    company_name: "",
    home_rate_per_kwh: "",
    currency: "EUR",
    is_active: true,
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [homeReport, setHomeReport] = useState<HomeChargingMonthlyReport | null>(null);
  const [homeReportLoading, setHomeReportLoading] = useState(false);
  const [financeSummary, setFinanceSummary] = useState<FinanceSummary | null>(null);
  const [financeTaxProfile, setFinanceTaxProfile] = useState<FinanceTaxProfile | null>(null);
  const [financeDocuments, setFinanceDocuments] = useState<FinanceDocument[]>([]);
  const [financeReconciliationBatches, setFinanceReconciliationBatches] = useState<FinanceReconciliationBatch[]>([]);
  const [financePayouts, setFinancePayouts] = useState<FinancePayout[]>([]);
  const [financeDunningCases, setFinanceDunningCases] = useState<FinanceDunningCase[]>([]);
  const [financeAuditEvents, setFinanceAuditEvents] = useState<FinanceAuditEvent[]>([]);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [financeEditOpen, setFinanceEditOpen] = useState(false);
  const [financeCountryCode, setFinanceCountryCode] = useState("");
  const [financeVatId, setFinanceVatId] = useState("");
  const [financeVatRate, setFinanceVatRate] = useState("0");
  const [financeReverseCharge, setFinanceReverseCharge] = useState(false);
  const [financeNotes, setFinanceNotes] = useState("");
  const [financeSaving, setFinanceSaving] = useState(false);
  const [financeActionLoadingId, setFinanceActionLoadingId] = useState<number | null>(null);
  const [financeActionError, setFinanceActionError] = useState<string | null>(null);
  const [financeBatchLoading, setFinanceBatchLoading] = useState(false);
  const [financePayoutProvider, setFinancePayoutProvider] = useState("manual");
  const [financePayoutExternalId, setFinancePayoutExternalId] = useState("");
  const [financePayoutGross, setFinancePayoutGross] = useState("");
  const [financePayoutFee, setFinancePayoutFee] = useState("0");
  const [financePayoutStatus, setFinancePayoutStatus] = useState("pending");

  const selectedDriver = useMemo(
    () => drivers.find((d) => d.id === driverId) ?? null,
    [drivers, driverId]
  );

  const loadDrivers = useCallback(async () => {
    setLoadingDrivers(true);
    try {
      const data = await api<DriverListResponse>(`${MGMT}/drivers`);
      setDrivers(fullDriversFromResponse(data));
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setLoadingDrivers(false);
    }
  }, [api, toast]);

  useEffect(() => {
    loadDrivers();
  }, [loadDrivers]);

  const loadFinance = useCallback(async () => {
    setFinanceLoading(true);
    try {
      const [summary, taxProfile, docs, batches, payouts, dunningCases, auditEvents] = await Promise.all([
        api<FinanceSummary>(`${MGMT}/reports/finance/summary`),
        api<FinanceTaxProfile | null>(`${MGMT}/reports/finance/tax-profile`),
        api<{ items: FinanceDocument[] }>(`${MGMT}/reports/finance/documents?limit=10`),
        api<FinanceReconciliationBatch[]>(`${MGMT}/reports/finance/reconciliation/batches`),
        api<FinancePayout[]>(`${MGMT}/reports/finance/payouts`),
        api<FinanceDunningCase[]>(`${MGMT}/reports/finance/dunning/cases`),
        api<FinanceAuditEvent[]>(`${MGMT}/reports/finance/audit-events?limit=10`),
      ]);
      setFinanceSummary(summary);
      setFinanceTaxProfile(taxProfile ?? null);
      setFinanceDocuments(docs?.items ?? []);
      setFinanceReconciliationBatches(batches ?? []);
      setFinancePayouts(payouts ?? []);
      setFinanceDunningCases(dunningCases ?? []);
      setFinanceAuditEvents(auditEvents ?? []);
      setFinanceActionError(null);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setFinanceLoading(false);
    }
  }, [api, toast]);

  useEffect(() => {
    loadFinance();
  }, [loadFinance]);

  const createMonthlyInvoice = async () => {
    if (!driverId) return;
    setInvoiceLoading(true);
    try {
      const created = await api<InvoiceRecord>(`${MGMT}/reports/invoices/monthly`, {
        method: "POST",
        body: JSON.stringify({ driver_id: driverId, month }),
      });
      setInvoice(created);
      toast.show("Monthly invoice generated");
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setInvoiceLoading(false);
    }
  };

  const createSessionInvoice = async () => {
    const parsed = Number.parseInt(sessionId, 10);
    if (!Number.isFinite(parsed)) return;
    setSessionInvoiceLoading(true);
    try {
      const created = await api<InvoiceRecord>(`${MGMT}/reports/invoices/sessions/${parsed}`, {
        method: "POST",
      });
      setInvoice(created);
      toast.show("Session invoice generated");
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSessionInvoiceLoading(false);
    }
  };

  const downloadInvoicePdf = async () => {
    if (!invoice) return;
    setPdfLoading(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: AUTH0_AUDIENCE || undefined },
      });
      const response = await fetch(`${MGMT}/reports/invoices/${invoice.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) {
        throw new Error(await response.text());
      }
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = objectUrl;
      a.download = `${invoice.invoice_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setPdfLoading(false);
    }
  };

  const saveHomeProfile = async () => {
    if (!driverId) return;
    const rate = Number.parseFloat(profile.home_rate_per_kwh);
    if (!Number.isFinite(rate)) {
      toast.show("Home rate must be a valid number", "error");
      return;
    }
    setSavingProfile(true);
    try {
      await api<HomeChargingProfile>(`${MGMT}/reports/home-reimbursement/profiles/${driverId}`, {
        method: "PUT",
        body: JSON.stringify({
          company_name: profile.company_name,
          home_rate_per_kwh: rate,
          currency: profile.currency || "EUR",
          is_active: profile.is_active,
        }),
      });
      toast.show("Home reimbursement profile saved");
    } catch (e) {
      toast.show((e as Error).message, "error");
    } finally {
      setSavingProfile(false);
    }
  };

  const loadHomeReport = async () => {
    if (!driverId) return;
    setHomeReportLoading(true);
    try {
      const report = await api<HomeChargingMonthlyReport>(
        `${MGMT}/reports/home-reimbursement/monthly?driver_id=${driverId}&month=${month}`
      );
      setHomeReport(report);
    } catch (e) {
      toast.show((e as Error).message, "error");
      setHomeReport(null);
    } finally {
      setHomeReportLoading(false);
    }
  };

  const openFinanceEdit = () => {
    setFinanceCountryCode(financeTaxProfile?.default_country_code ?? "");
    setFinanceVatId(financeTaxProfile?.vat_id ?? "");
    setFinanceVatRate(String(financeTaxProfile?.default_vat_rate_percent ?? 0));
    setFinanceReverseCharge(financeTaxProfile?.reverse_charge_enabled ?? false);
    setFinanceNotes(financeTaxProfile?.notes ?? "");
    setFinanceActionError(null);
    setFinanceEditOpen(true);
  };

  const saveFinanceTaxProfile = async () => {
    const vatRate = Number.parseFloat(financeVatRate);
    if (!Number.isFinite(vatRate) || vatRate < 0) {
      setFinanceActionError("VAT rate must be a valid non-negative number");
      return;
    }
    setFinanceSaving(true);
    setFinanceActionError(null);
    try {
      const updated = await api<FinanceTaxProfile>(`${MGMT}/reports/finance/tax-profile`, {
        method: "PUT",
        body: JSON.stringify({
          default_country_code: financeCountryCode.trim() || null,
          vat_id: financeVatId.trim() || null,
          default_vat_rate_percent: vatRate,
          reverse_charge_enabled: financeReverseCharge,
          is_active: true,
          notes: financeNotes.trim() || null,
        }),
      });
      setFinanceTaxProfile(updated);
      setFinanceEditOpen(false);
      await loadFinance();
    } catch (e) {
      setFinanceActionError((e as Error).message);
    } finally {
      setFinanceSaving(false);
    }
  };

  const runFinanceReconciliation = async () => {
    setFinanceBatchLoading(true);
    setFinanceActionError(null);
    try {
      await api(`${MGMT}/reports/finance/reconciliation/run`, {
        method: "POST",
        body: JSON.stringify({ source_system: "manual", statement_reference: "reports", currency: "EUR" }),
      });
      await loadFinance();
    } catch (e) {
      setFinanceActionError((e as Error).message);
    } finally {
      setFinanceBatchLoading(false);
    }
  };

  const runFinanceDunning = async () => {
    setFinanceBatchLoading(true);
    setFinanceActionError(null);
    try {
      await api(`${MGMT}/reports/finance/dunning/run`, { method: "POST" });
      await loadFinance();
    } catch (e) {
      setFinanceActionError((e as Error).message);
    } finally {
      setFinanceBatchLoading(false);
    }
  };

  const createFinanceCreditNote = async (documentId: number) => {
    setFinanceActionLoadingId(documentId);
    setFinanceActionError(null);
    try {
      await api(`${MGMT}/reports/finance/documents/${documentId}/credit-note`, { method: "POST" });
      await loadFinance();
    } catch (e) {
      setFinanceActionError((e as Error).message);
    } finally {
      setFinanceActionLoadingId(null);
    }
  };

  const createFinanceRefund = async (documentId: number) => {
    setFinanceActionLoadingId(documentId);
    setFinanceActionError(null);
    try {
      await api(`${MGMT}/reports/finance/documents/${documentId}/refund`, { method: "POST" });
      await loadFinance();
    } catch (e) {
      setFinanceActionError((e as Error).message);
    } finally {
      setFinanceActionLoadingId(null);
    }
  };

  const createFinancePayout = async () => {
    const gross = Number.parseFloat(financePayoutGross);
    const fee = Number.parseFloat(financePayoutFee);
    if (!financePayoutExternalId.trim()) {
      setFinanceActionError("Payout reference is required");
      return;
    }
    if (!Number.isFinite(gross) || gross <= 0 || !Number.isFinite(fee) || fee < 0) {
      setFinanceActionError("Payout amount and fee must be valid numbers");
      return;
    }
    setFinanceBatchLoading(true);
    setFinanceActionError(null);
    try {
      await api(`${MGMT}/reports/finance/payouts`, {
        method: "POST",
        body: JSON.stringify({
          provider: financePayoutProvider.trim() || "manual",
          provider_payout_id: financePayoutExternalId.trim(),
          currency: "EUR",
          amount_gross: gross,
          fee_amount: fee,
          status: financePayoutStatus,
          payload: {},
        }),
      });
      setFinancePayoutExternalId("");
      setFinancePayoutGross("");
      setFinancePayoutFee("0");
      await loadFinance();
    } catch (e) {
      setFinanceActionError((e as Error).message);
    } finally {
      setFinanceBatchLoading(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              select
              label="Driver"
              value={driverId}
              onChange={(e) => setDriverId(Number(e.target.value) || "")}
              fullWidth
              disabled={loadingDrivers}
            >
              <MenuItem value="">Select driver…</MenuItem>
              {drivers.map((d) => (
                <MenuItem key={d.id} value={d.id}>
                  {d.name} ({d.email})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="month"
              label="Month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card variant="outlined">
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="center">
            <Typography variant="h6" sx={{ flexGrow: 1 }}>Finance Operations</Typography>
            <Button size="small" variant="outlined" onClick={openFinanceEdit}>
              Edit tax profile
            </Button>
            <Button size="small" variant="outlined" onClick={runFinanceReconciliation} disabled={financeBatchLoading}>
              Run reconciliation
            </Button>
            <Button size="small" variant="outlined" onClick={runFinanceDunning} disabled={financeBatchLoading}>
              Run dunning
            </Button>
            {financeSummary && (
              <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                <Typography variant="body2">Open documents: {financeSummary.open_documents}</Typography>
                <Typography variant="body2">Overdue: {financeSummary.overdue_documents}</Typography>
                <Typography variant="body2">Open batches: {financeSummary.open_reconciliation_batches}</Typography>
                <Typography variant="body2">Dunning: {financeSummary.open_dunning_cases}</Typography>
                <Typography variant="body2">
                  Outstanding: {financeSummary.outstanding_amount.toFixed(2)}
                </Typography>
              </Stack>
            )}
          </Stack>
          {financeActionError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {financeActionError}
            </Alert>
          )}
          {financeLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : (
            <>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {financeTaxProfile
                  ? `Tax profile: ${financeTaxProfile.default_country_code ?? "—"} · VAT ${financeTaxProfile.default_vat_rate_percent.toFixed(2)}%${financeTaxProfile.reverse_charge_enabled ? " · reverse charge" : ""}`
                  : "No tax profile configured."}
              </Typography>
              <TableContainer sx={{ mt: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Document</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Gross</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {financeDocuments.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell>{doc.invoice_number}</TableCell>
                        <TableCell>{doc.document_type}</TableCell>
                        <TableCell>{doc.status}</TableCell>
                        <TableCell align="right">
                          {doc.total_gross.toFixed(2)} {doc.currency}
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={financeActionLoadingId === doc.id}
                              onClick={() => createFinanceCreditNote(doc.id)}
                            >
                              Credit note
                            </Button>
                            <Button
                              size="small"
                              variant="outlined"
                              disabled={financeActionLoadingId === doc.id}
                              onClick={() => createFinanceRefund(doc.id)}
                            >
                              Refund
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                    {financeDocuments.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5}>
                          <Typography variant="body2" color="text.secondary">
                            No finance documents yet.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Payout tracking</Typography>
              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField size="small" label="Provider" value={financePayoutProvider} onChange={(e) => setFinancePayoutProvider(e.target.value)} />
                <TextField size="small" label="Payout ref" value={financePayoutExternalId} onChange={(e) => setFinancePayoutExternalId(e.target.value)} />
                <TextField size="small" label="Gross" type="number" value={financePayoutGross} onChange={(e) => setFinancePayoutGross(e.target.value)} />
                <TextField size="small" label="Fee" type="number" value={financePayoutFee} onChange={(e) => setFinancePayoutFee(e.target.value)} />
                <Select size="small" value={financePayoutStatus} onChange={(e) => setFinancePayoutStatus(e.target.value)}>
                  <MenuItem value="pending">pending</MenuItem>
                  <MenuItem value="paid">paid</MenuItem>
                  <MenuItem value="failed">failed</MenuItem>
                </Select>
                <Button size="small" variant="contained" onClick={createFinancePayout} disabled={financeBatchLoading}>Add payout</Button>
              </Stack>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Ref</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Net</TableCell>
                    <TableCell>Created</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financePayouts.slice(0, 5).map((payout) => (
                    <TableRow key={payout.id}>
                      <TableCell>{payout.provider_payout_id}</TableCell>
                      <TableCell>{payout.status}</TableCell>
                      <TableCell align="right">{payout.amount_net.toFixed(2)} {payout.currency}</TableCell>
                      <TableCell>{new Date(payout.created_at).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Reconciliation batches</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Reference</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="right">Variance</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financeReconciliationBatches.slice(0, 5).map((batch) => (
                    <TableRow key={batch.id}>
                      <TableCell>{batch.statement_reference ?? `batch-${batch.id}`}</TableCell>
                      <TableCell>{batch.status}</TableCell>
                      <TableCell align="right">{batch.variance_amount.toFixed(2)} {batch.currency}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Dunning cases</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Invoice</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Step</TableCell>
                    <TableCell align="right">Overdue</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financeDunningCases.slice(0, 5).map((dunningCase) => (
                    <TableRow key={dunningCase.id}>
                      <TableCell>{dunningCase.invoice_id}</TableCell>
                      <TableCell>{dunningCase.status}</TableCell>
                      <TableCell>{dunningCase.dunning_step}</TableCell>
                      <TableCell align="right">{dunningCase.overdue_amount.toFixed(2)} {dunningCase.currency}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Recent finance audit events</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>When</TableCell>
                    <TableCell>Entity</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financeAuditEvents.slice(0, 8).map((event) => (
                    <TableRow key={event.id}>
                      <TableCell>{new Date(event.created_at).toLocaleString()}</TableCell>
                      <TableCell>{event.entity_type}</TableCell>
                      <TableCell>{event.action}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={financeEditOpen} onClose={() => !financeSaving && setFinanceEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tax Profile</DialogTitle>
        <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
          <TextField
            label="Default country code"
            value={financeCountryCode}
            onChange={(e) => setFinanceCountryCode(e.target.value.toUpperCase())}
            disabled={financeSaving}
            fullWidth
          />
          <TextField
            label="VAT ID"
            value={financeVatId}
            onChange={(e) => setFinanceVatId(e.target.value)}
            disabled={financeSaving}
            fullWidth
          />
          <TextField
            label="Default VAT rate"
            type="number"
            value={financeVatRate}
            onChange={(e) => setFinanceVatRate(e.target.value)}
            disabled={financeSaving}
            inputProps={{ min: 0, step: "any" }}
            fullWidth
          />
          <Select
            value={financeReverseCharge ? "enabled" : "disabled"}
            onChange={(e) => setFinanceReverseCharge(e.target.value === "enabled")}
            disabled={financeSaving}
            fullWidth
          >
            <MenuItem value="disabled">Standard VAT</MenuItem>
            <MenuItem value="enabled">Reverse charge</MenuItem>
          </Select>
          <TextField
            label="Notes"
            value={financeNotes}
            onChange={(e) => setFinanceNotes(e.target.value)}
            disabled={financeSaving}
            multiline
            minRows={2}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFinanceEditOpen(false)} disabled={financeSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={saveFinanceTaxProfile} disabled={financeSaving}>
            {financeSaving ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <ReceiptLongIcon fontSize="small" />
                <Typography variant="h6">Invoices</Typography>
              </Stack>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1} sx={{ mb: 2 }}>
                <Button
                  variant="contained"
                  onClick={createMonthlyInvoice}
                  disabled={!driverId || invoiceLoading}
                >
                  Generate monthly invoice
                </Button>
                <TextField
                  label="Session ID"
                  size="small"
                  value={sessionId}
                  onChange={(e) => setSessionId(e.target.value)}
                />
                <Button
                  variant="outlined"
                  onClick={createSessionInvoice}
                  disabled={sessionInvoiceLoading || !sessionId.trim()}
                >
                  Generate session invoice
                </Button>
              </Stack>

              {invoiceLoading || sessionInvoiceLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : !invoice ? (
                <Typography color="text.secondary">No invoice generated in this view yet.</Typography>
              ) : (
                <Stack spacing={1}>
                  <Typography sx={{ fontWeight: 600 }}>
                    {invoice.invoice_number} ({invoice.invoice_type})
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Driver: {selectedDriver?.name ?? invoice.driver_id} · Created: {fmtDate(invoice.created_at)}
                  </Typography>
                  <Typography variant="body2">
                    Net {invoice.subtotal_net.toFixed(2)} {invoice.currency} + Tax {invoice.tax_amount.toFixed(2)}{" "}
                    {invoice.currency} = {invoice.total_gross.toFixed(2)} {invoice.currency}
                  </Typography>
                  <Button
                    startIcon={<DownloadIcon />}
                    onClick={downloadInvoicePdf}
                    disabled={pdfLoading}
                    sx={{ alignSelf: "flex-start" }}
                  >
                    Download PDF
                  </Button>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Session</TableCell>
                          <TableCell>Jurisdiction</TableCell>
                          <TableCell align="right">kWh</TableCell>
                          <TableCell align="right">Tax %</TableCell>
                          <TableCell align="right">Gross</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {invoice.line_items.map((item) => (
                          <TableRow key={`${invoice.id}-${item.session_id}`}>
                            <TableCell>{item.session_id}</TableCell>
                            <TableCell>{item.jurisdiction}</TableCell>
                            <TableCell align="right">{item.energy_kwh.toFixed(2)}</TableCell>
                            <TableCell align="right">{item.tax_rate_percent.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {item.gross_amount.toFixed(2)} {invoice.currency}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Card variant="outlined">
            <CardContent>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
                <SavingsIcon fontSize="small" />
                <Typography variant="h6">Home reimbursement</Typography>
              </Stack>

              <Stack spacing={1.5} sx={{ mb: 2 }}>
                <TextField
                  label="Company name"
                  value={profile.company_name}
                  onChange={(e) => setProfile((v) => ({ ...v, company_name: e.target.value }))}
                />
                <TextField
                  label="Home rate per kWh"
                  type="number"
                  value={profile.home_rate_per_kwh}
                  onChange={(e) => setProfile((v) => ({ ...v, home_rate_per_kwh: e.target.value }))}
                  inputProps={{ step: "any" }}
                />
                <TextField
                  label="Currency"
                  value={profile.currency}
                  onChange={(e) => setProfile((v) => ({ ...v, currency: e.target.value.toUpperCase() }))}
                />
                <Button
                  variant="outlined"
                  onClick={saveHomeProfile}
                  disabled={!driverId || !profile.company_name || !profile.home_rate_per_kwh || savingProfile}
                >
                  Save reimbursement profile
                </Button>
                <Button
                  variant="contained"
                  onClick={loadHomeReport}
                  disabled={!driverId || homeReportLoading}
                >
                  Generate monthly reimbursement report
                </Button>
              </Stack>

              {homeReportLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress size={24} />
                </Box>
              ) : homeReport ? (
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    {homeReport.company_name} · {homeReport.month}
                  </Typography>
                  <Typography variant="body2">
                    {homeReport.total_energy_kwh.toFixed(2)} kWh × {homeReport.home_rate_per_kwh.toFixed(4)}{" "}
                    {homeReport.currency}/kWh
                  </Typography>
                  <Typography sx={{ fontWeight: 600 }}>
                    Total reimbursement: {homeReport.total_reimbursement_amount.toFixed(2)} {homeReport.currency}
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Session</TableCell>
                          <TableCell>Charger</TableCell>
                          <TableCell align="right">kWh</TableCell>
                          <TableCell align="right">Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {homeReport.sessions.map((session) => (
                          <TableRow key={session.session_id}>
                            <TableCell>{session.session_id}</TableCell>
                            <TableCell sx={{ fontFamily: "monospace" }}>{session.charge_point_id}</TableCell>
                            <TableCell align="right">{session.energy_kwh.toFixed(2)}</TableCell>
                            <TableCell align="right">
                              {session.reimbursement_amount.toFixed(2)} {homeReport.currency}
                            </TableCell>
                          </TableRow>
                        ))}
                        {homeReport.sessions.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              No home charging sessions found for selected month.
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              ) : (
                <Typography color="text.secondary">
                  Save a reimbursement profile and generate a monthly report.
                </Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Stack>
  );
}
